<template>
  <div class="header-search" ref="searchContainer" :class="{ 'is-open': isOpen }">
    <div class="search-pill" :class="{ 'is-expanded': isOpen }">
      <button class="search-pill__icon" @click="toggleSearch">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>
      <div class="search-pill__input-area" v-show="isOpen">
        <input
          ref="searchInput"
          v-model="searchTerm"
          @input="debouncedSearch"
          @keydown.escape="closeSearch"
          type="text"
          placeholder="Search..."
          autocomplete="off"
        />
        <button v-if="searchTerm" class="search-pill__clear" @click="clearSearch">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
    <transition name="search-slide">
      <div v-if="isOpen && (results.length || isLoading || noResults)" class="search-dropdown">
        <div class="search-results" v-if="results.length">
          <a
            v-for="result in results"
            :key="result.id + (result.matchedSku || '')"
            class="search-result-item"
            @click.prevent="goToResult(result)"
          >
            <div class="result-info">
              <span class="result-title" v-html="result.title.rendered"></span>
              <span class="result-sku" v-if="result.matchedSku">SKU #{{ result.matchedSku }}</span>
              <span class="result-sku" v-else-if="result.resultType === 'resource'">{{ result.resourceSection }}</span>
              <span class="result-sku" v-else-if="result.resultType === 'quicklink'">{{ result.quicklinkSubtitle }}</span>
              <span class="result-sku" v-else-if="result.resultType === 'project'">Project</span>
            </div>
            <svg v-if="result.resultType === 'resource'" class="result-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            <svg v-else class="result-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </a>
        </div>
        <div class="search-status" v-if="isLoading">
          <span class="search-spinner"></span> Searching...
        </div>
        <div class="search-status" v-else-if="noResults">No results found</div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { fetchItems } from '@/api'

const router = useRouter()

const searchContainer = ref(null)
const searchInput = ref(null)
const isOpen = ref(false)
const searchTerm = ref('')
const results = ref([])
const noResults = ref(false)
const isLoading = ref(false)

let debounceTimer = null
let allProductsCache = null
let productsLoadPromise = null
let resourcesCache = null
let resourcesLoadPromise = null
let projectsCache = null
let projectsLoadPromise = null

// Extract SKU numbers from the ACF size wysiwyg field text
// Field contains text like "1117: Vertical – L 20" W 13" H 40" | 74 lbs..."
const extractSkus = (sizeText) => {
  if (!sizeText) return []
  const stripped = sizeText.replace(/<[^>]*>/g, ' ')
  const matches = stripped.match(/\b(\d{3,5})\s*:/g)
  if (!matches) return []
  return matches.map(m => m.replace(':', '').trim())
}

const loadAllProducts = () => {
  if (productsLoadPromise) return productsLoadPromise
  productsLoadPromise = fetchItems({
    type: 'products',
    params: { per_page: 100, _fields: 'id,slug,title,acf' }
  }).then(response => {
    if (response && response.data) {
      allProductsCache = response.data
    }
  }).catch(() => {})
  return productsLoadPromise
}

const loadResources = () => {
  if (resourcesLoadPromise) return resourcesLoadPromise
  resourcesLoadPromise = fetchItems({
    type: 'pages',
    params: { slug: 'resources' }
  }).then(response => {
    if (response && response.data && response.data.length) {
      const page = response.data[0]
      const modules = page.acf?.modules || []
      const items = []
      for (const mod of modules) {
        if (mod.acf_fc_layout === 'product_info') {
          const specsCats = mod.specs_categories || []
          for (const specCat of specsCats) {
            const sectionTitle = specCat.spec_title || ''
            const categories = specCat.categories || []
            for (const cat of categories) {
              const catTitle = cat.category_title || ''
              if (cat.spec_sheet && cat.sub_option_spec) {
                // Category-level spec sheet
                items.push({
                  title: catTitle,
                  section: sectionTitle,
                  url: cat.sub_option_spec.url || cat.sub_option_spec,
                  type: 'resource'
                })
              }
              const subOptions = cat.sub_option || []
              for (const sub of subOptions) {
                const subTitle = sub.sub_option_title || ''
                const specFile = sub.sub_option_spec
                if (specFile && (specFile.url || typeof specFile === 'string')) {
                  items.push({
                    title: subTitle,
                    section: sectionTitle,
                    category: catTitle,
                    url: specFile.url || specFile,
                    type: 'resource'
                  })
                }
              }
            }
          }
        }
        // Also check for terms/conditions PDFs
        if (mod.acf_fc_layout === 'container' && mod.inner_content) {
          for (const inner of mod.inner_content) {
            if (inner.acf_fc_layout === 'terms_conditions' && inner.terms_area) {
              for (const term of inner.terms_area) {
                if (term.file && term.title) {
                  items.push({
                    title: term.title,
                    section: 'Documents',
                    url: term.file.url || term.file,
                    type: 'resource'
                  })
                }
              }
            }
          }
        }
      }
      resourcesCache = items
    }
  }).catch(() => {})
  return resourcesLoadPromise
}

const loadProjects = () => {
  if (projectsLoadPromise) return projectsLoadPromise
  projectsLoadPromise = fetchItems({
    type: 'projects',
    params: { per_page: 100, _fields: 'id,slug,title,link' }
  }).then(response => {
    if (response && response.data) {
      projectsCache = response.data
    }
  }).catch(() => {})
  return projectsLoadPromise
}

onMounted(() => {
  loadAllProducts()
  loadResources()
  loadProjects()
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
})

const toggleSearch = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      searchInput.value?.focus()
    })
  } else {
    resetSearch()
  }
}

const closeSearch = () => {
  isOpen.value = false
  resetSearch()
}

const clearSearch = () => {
  searchTerm.value = ''
  results.value = []
  noResults.value = false
  searchInput.value?.focus()
}

const resetSearch = () => {
  searchTerm.value = ''
  results.value = []
  noResults.value = false
  isLoading.value = false
  if (debounceTimer) clearTimeout(debounceTimer)
}

const debouncedSearch = () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (!searchTerm.value || searchTerm.value.length < 2) {
    results.value = []
    noResults.value = false
    isLoading.value = false
    return
  }
  isLoading.value = true
  debounceTimer = setTimeout(() => {
    performSearch()
  }, 300)
}

const performSearch = async () => {
  const term = searchTerm.value.trim()
  if (!term || term.length < 2) return

  try {
  // Ensure data is loaded before searching
  await Promise.all([
    !allProductsCache && productsLoadPromise ? productsLoadPromise : Promise.resolve(),
    !resourcesCache && resourcesLoadPromise ? resourcesLoadPromise : Promise.resolve(),
    !projectsCache && projectsLoadPromise ? projectsLoadPromise : Promise.resolve()
  ])

  const termLower = term.toLowerCase()
  const combined = []
  const seenIds = new Set()

  // 1. Title search via WP REST API
  try {
    const response = await fetchItems({
      type: 'products',
      params: { search: term, per_page: 8 }
    })
    if (response && response.data) {
      for (const item of response.data) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id)
          combined.push({ ...item, resultType: 'product' })
        }
      }
    }
  } catch (e) {
    // API error — continue with local search
  }

  // 2. SKU search from ACF size field + local title/slug matching
  if (allProductsCache) {
    for (const product of allProductsCache) {
      if (seenIds.has(product.id)) continue

      // Search the ACF size text for SKU numbers
      const sizeText = product.acf?.['acf-single-product-options-size'] || ''
      const skus = extractSkus(sizeText)
      const matchedSku = skus.find(sku => sku.includes(termLower) || termLower.includes(sku))
      if (matchedSku) {
        seenIds.add(product.id)
        combined.push({ ...product, matchedSku, resultType: 'product' })
        continue
      }

      // Also check if the raw size text contains the search term
      const sizeStripped = sizeText.replace(/<[^>]*>/g, ' ').toLowerCase()
      if (sizeStripped.includes(termLower)) {
        seenIds.add(product.id)
        combined.push({ ...product, resultType: 'product' })
        continue
      }
    }

    // 3. Title/slug matching
    for (const product of allProductsCache) {
      if (seenIds.has(product.id)) continue
      const title = (product.title?.rendered || '').toLowerCase()
      const slug = (product.slug || '').toLowerCase()
      if (title.includes(termLower) || slug.includes(termLower)) {
        seenIds.add(product.id)
        combined.push({ ...product, resultType: 'product' })
      }
    }
  }

  // 4. Search resources/spec sheets
  if (resourcesCache) {
    for (const resource of resourcesCache) {
      const title = (resource.title || '').toLowerCase()
      const section = (resource.section || '').toLowerCase()
      const category = (resource.category || '').toLowerCase()
      if (title.includes(termLower) || section.includes(termLower) || category.includes(termLower)) {
        const key = 'resource-' + resource.title + resource.url
        if (!seenIds.has(key)) {
          seenIds.add(key)
          combined.push({
            id: key,
            title: { rendered: resource.title },
            resultType: 'resource',
            resourceSection: resource.category || resource.section,
            resourceUrl: resource.url
          })
        }
      }
    }
  }

  // 5. Quick links (keyword → page mappings)
  const quickLinks = [
    { keywords: ['sample', 'samples', 'finish', 'finishes', 'color', 'colors'], title: 'Colors & Finishes', subtitle: 'Order Samples', url: '/finishes/' }
  ]
  for (const link of quickLinks) {
    if (link.keywords.some(kw => termLower.includes(kw) || kw.includes(termLower))) {
      const key = 'quicklink-' + link.url
      if (!seenIds.has(key)) {
        seenIds.add(key)
        combined.unshift({
          id: key,
          title: { rendered: link.title },
          resultType: 'quicklink',
          quicklinkSubtitle: link.subtitle,
          quicklinkUrl: link.url
        })
      }
    }
  }

  // 6. Search projects
  if (projectsCache) {
    for (const project of projectsCache) {
      const title = (project.title?.rendered || '').toLowerCase()
      const slug = (project.slug || '').toLowerCase()
      if (title.includes(termLower) || slug.includes(termLower)) {
        const key = 'project-' + project.id
        if (!seenIds.has(key)) {
          seenIds.add(key)
          combined.push({
            ...project,
            id: key,
            resultType: 'project',
            projectLink: project.link
          })
        }
      }
    }
  }

  if (combined.length) {
    results.value = combined.slice(0, 12)
    noResults.value = false
  } else {
    results.value = []
    noResults.value = true
  }
  } catch (e) {
    console.error('Search error:', e)
    results.value = []
    noResults.value = true
  } finally {
    isLoading.value = false
  }
}

const goToResult = (result) => {
  if (result.resultType === 'quicklink' && result.quicklinkUrl) {
    window.location.href = result.quicklinkUrl
  } else if (result.resultType === 'resource' && result.resourceUrl) {
    window.open(result.resourceUrl, '_blank')
  } else if (result.resultType === 'project' && result.projectLink) {
    window.location.href = result.projectLink
  } else {
    router.push(`/products/${result.slug}`)
  }
  closeSearch()
}

const handleClickOutside = (e) => {
  if (isOpen.value && searchContainer.value && !searchContainer.value.contains(e.target)) {
    closeSearch()
  }
}

const handleEscape = (e) => {
  if (e.key === 'Escape' && isOpen.value) {
    closeSearch()
  }
}

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
  if (debounceTimer) clearTimeout(debounceTimer)
})
</script>

<style lang="scss" scoped>
.header-search {
  @apply relative flex items-center;

  .search-pill {
    @apply flex items-center;
    background-color: rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    border-radius: 6px;
    height: 40px;
    transition: width 0.3s ease, border-radius 0.3s ease;
    overflow: hidden;

    @media (min-width: 1025px) {
      height: 58px;
    }

    &__icon {
      @apply flex items-center justify-center flex-shrink-0 cursor-pointer;
      width: 40px;
      height: 40px;
      color: currentColor;
      transition: opacity 0.2s ease;

      @media (min-width: 1025px) {
        width: 58px;
        height: 58px;
      }

      &:hover {
        @apply opacity-60;
      }
    }

    &__input-area {
      @apply flex items-center;
      padding-right: 12px;

      input {
        @apply font-FHOscar;
        font-size: 14px;
        background: transparent;
        color: currentColor;
        border: none;
        outline: none;
        box-shadow: none;
        width: 180px;
        -webkit-appearance: none;

        @media (min-width: 1025px) {
          font-size: 16px;
          width: 220px;
        }

        &::placeholder {
          color: rgba(0, 0, 0, 0.4);
        }
      }
    }

    &__clear {
      @apply flex items-center justify-center flex-shrink-0 cursor-pointer ml-1;
      width: 20px;
      height: 20px;
      opacity: 0.4;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .search-dropdown {
    @apply absolute z-50;
    top: calc(100% + 6px);
    right: 0;
    min-width: 280px;
    width: 100%;

    .search-results {
      @apply overflow-hidden;
      background-color: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(30px);
      -webkit-backdrop-filter: blur(30px);
      border-radius: 6px;
      max-height: 340px;
      overflow-y: auto;

      .search-result-item {
        @apply flex items-center justify-between px-4 py-3 font-FHOscar cursor-pointer transition-all duration-200;
        color: currentColor;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        &:last-child {
          border-bottom: none;
        }
        &:hover {
          background: rgba(0, 0, 0, 0.06);
        }
        .result-info {
          @apply flex flex-col;
          .result-title {
            font-size: 14px;
            @media (min-width: 1025px) {
              font-size: 16px;
            }
          }
          .result-sku {
            font-size: 12px;
            opacity: 0.5;
            margin-top: 2px;
          }
        }
        .result-arrow {
          @apply flex-shrink-0 ml-3 transition-opacity duration-200;
          opacity: 0;
        }
        .result-icon {
          @apply flex-shrink-0 ml-3;
          opacity: 0.4;
        }
        &:hover .result-arrow,
        &:hover .result-icon {
          opacity: 0.5;
        }
      }
    }

    .search-status {
      @apply px-4 py-3 font-FHOscar flex items-center gap-2;
      font-size: 14px;
      opacity: 0.5;
      background-color: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(30px);
      -webkit-backdrop-filter: blur(30px);
      border-radius: 6px;
      margin-top: 4px;
    }

    .search-spinner {
      @apply inline-block w-4 h-4 rounded-full flex-shrink-0;
      border: 2px solid rgba(0, 0, 0, 0.15);
      border-top-color: rgba(0, 0, 0, 0.5);
      animation: spin 0.6s linear infinite;
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.search-slide-enter-active {
  transition: all 0.2s ease-out;
}
.search-slide-leave-active {
  transition: all 0.15s ease-in;
}
.search-slide-enter-from,
.search-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 1024px) {
  .header-search .search-dropdown {
    position: fixed;
    left: 16px;
    right: 16px;
    top: 72px;
    width: auto;
  }
}
</style>
